/* eslint-disable camelcase */
import React from 'react'
import PropTypes from 'prop-types'
import Card from '@material-ui/core/Card'
import CardMedia from '@material-ui/core/CardMedia'
import CardContent from '@material-ui/core/CardContent'
import Typography from '@material-ui/core/Typography'
import Box from '@material-ui/core/Box'
import { formattedTimeWithTimezone } from '@utils/dateUtils'
import ButtonCta from '@components/atoms/button-cta'
import IconFlagFilled from '@components/atoms/icons/icon-flag-filled'
import {
  IconPrice,
} from '@components/atoms/icons'
import { centsToDollarsFormatted } from '@lib/conversions'
import useStyles, { CardIcon } from './program-card.styles'

const JRLLogoIcon = 'https://images.ctfassets.net/56u5qdsjym8c/7sy1Gw4RKBLC9d8ylCdMOs/ec28c360c5b398789a2cb0fd48525519/jr_league.svg'
const FGLogoIcon = 'https://images.ctfassets.net/56u5qdsjym8c/4ECZPKJ1X5na6EW0xH33X3/48e67d481aac9b713c073f712c546516/family-golf-logo__3_.svg'

function ProgramCardAlgolia({ event, dataAttrs = {}, openInNewTab = false }) {
  const classes = useStyles()
  const {
    title,
    start_date_time,
    end_date_time,
    price_in_cents,
    coach_facility_timezone,
    coach_facility_name,
    tags,
    url,
    additional_fees,
    total_price_in_cents,
  } = event

  const timePeriod = formattedTimeWithTimezone(
    start_date_time,
    end_date_time,
    coach_facility_timezone,
  )

  const eventCost = total_price_in_cents || price_in_cents
  const totalPriceInDollars = centsToDollarsFormatted(eventCost + additional_fees)

  const isJRL = tags.includes('PGA Jr. League')
  const logoUrl = isJRL ? JRLLogoIcon : FGLogoIcon
  const programImage = isJRL
    ? 'https://images.ctfassets.net/56u5qdsjym8c/1yoePOUAugYt2Za7y7Zmca/394ca263cad1c11e1344efb13ed2e0e3/JRL_programs_bg.png?h=250'
    : 'https://images.ctfassets.net/56u5qdsjym8c/5SWGO8vvLshekEP1mGo826/0345e17afa5d282c0bafb50174b06bf5/FG_programs_bg.png?h=250'

  const handleCardClick = (e) => {
    if (!url) return
    if (typeof e?.target?.closest === 'function') {
      const interactive = e.target.closest('a, button, [role="button"]')
      if (interactive && interactive !== e.currentTarget) return
    }
    if (openInNewTab) {
      window.open(url, '_blank', 'noopener,noreferrer')
    } else {
      window.location.href = url
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleCardClick(e)
    }
  }

  return (
    <Card
      className={`${classes.groupEvent}`}
      onClick={url ? handleCardClick : undefined}
      role={url ? 'link' : undefined}
      tabIndex={url ? 0 : undefined}
      onKeyDown={url ? handleKeyDown : undefined}
      {...dataAttrs}
    >
      <CardMedia
        className={`${classes.media}`}
        image={programImage}
      />

      <CardIcon
        style={{
          backgroundImage: `url(${logoUrl})`,
          // Jr. League mark reads large at 80% like FG; tighter scale adds breathing room in the badge.
          ...(isJRL ? { backgroundSize: '62%' } : {}),
        }}
      />

      <Box style={{ flexGrow: '1', display: 'flex', flexDirection: 'column' }}>
        <CardContent className={classes.cardContent}>
          <Box>
            {start_date_time && end_date_time
              ? (
                <Typography variant="caption" className={classes.caption}>
                  {timePeriod}
                </Typography>
              )
              : (
                <Typography variant="caption" className={classes.caption}>
                  Program Dates TBD
                </Typography>
              )}
            <Typography
              variant="h5"
              color="primary"
              className={classes.cardSubtitle}
            >
              {title}
            </Typography>
          </Box>
          <Box>
            {coach_facility_name != null && (
              <Box display="flex" mb={1}>
                <IconFlagFilled />
                <Typography
                  variant="caption"
                  className={classes.caption}
                  style={{ marginLeft: '12px' }}
                >
                  {coach_facility_name}
                </Typography>
              </Box>
            )}
            {price_in_cents != null && (
              <Box display="flex">
                <IconPrice />
                <Typography
                  variant="caption"
                  className={classes.caption}
                  style={{ marginLeft: '12px' }}
                >
                  {totalPriceInDollars} / {isJRL ? 'player' : 'team'} + 3% processing fee*
                </Typography>
              </Box>
            )}
          </Box>
        </CardContent>
      </Box>
      <ButtonCta
        variant="text"
        arrow
        {...(!openInNewTab && { href: url })}
        {...(openInNewTab && { externalLink: url })}
        className={classes.ctaButton}
        dataAttrs={{ ...dataAttrs }}
        onClick={(e) => e.stopPropagation()}
      >
        View Program
      </ButtonCta>
    </Card>
  )
}

ProgramCardAlgolia.propTypes = {
  event: PropTypes.shape({
    title: PropTypes.string.isRequired,
    featuredImageUrl: PropTypes.string,
    startDateTime: PropTypes.string,
    endDateTime: PropTypes.string,
    start_date_time: PropTypes.string,
    end_date_time: PropTypes.string,
    maxAttendance: PropTypes.number,
    slug: PropTypes.string,
    registrationCount: PropTypes.number,
    priceInCents: PropTypes.number,
    price_in_cents: PropTypes.number,
    coach_facility_timezone: PropTypes.string,
    coach_facility_name: PropTypes.string,
    tags: PropTypes.arrayOf(PropTypes.string),
    url: PropTypes.string,
    additional_fees: PropTypes.number,
    total_price_in_cents: PropTypes.number,
    sessions: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
      }),
    ),
    coachFacility: PropTypes.shape({
      name: PropTypes.string,
      id: PropTypes.string,
      timezone: PropTypes.string,
    }),
  }).isRequired,
  dataAttrs: PropTypes.shape({}),
  openInNewTab: PropTypes.bool,
}

export default ProgramCardAlgolia
